/**
 * The places, rather than the nights.
 *
 * WP 3.4c's own answer to what a fifth tab could be, once Saved folded into
 * Lists: venues have photographs now, so a screen of places is a screen of
 * pictures rather than a list of names.
 *
 * It answers a question the feed cannot. The feed is ordered by when and by
 * taste, so "what is on at the Konzerthaus" means scrolling and filtering;
 * here it is one row. It is also where a building's halls stop being separate
 * things — the Philharmonie is one place with a Großer Saal and a Foyer under
 * it, and the feed has no reason to say so.
 *
 * ## WP 6.3: not a tab any more
 *
 * Places is Browse's other mode now (`Browse.tsx` owns the Events/Places
 * segmented control, the chip row and the search chrome above this
 * component, and calls `placesOf` below itself) — this file keeps the two
 * things that were never chrome: the ordering that turns a list of buildings
 * into what's on screen, and the place page underneath it. `placesOf`'s own
 * rows run through Browse's shared filter (dismissed, language, hidden
 * lanes, already-happened, away) before grouping — the same rows Events
 * mode windows by day, grouped by building instead. That is the whole point
 * of the merge: one filtered feed, two ways of looking at it, rather than
 * the two screens quietly answering "what's on" with two different sets of
 * rows.
 *
 * The old index sectioned by kind (`../lib/placeGroups`'s seven groups,
 * still `PlaceMap`'s own pin colouring). WP 6.3's brief asks for a flatter
 * list instead — every place with something on today first, then by its
 * best score, one representative row and a "N more" link under each — so
 * `GROUPS`/`groupOf` are gone from here; `PlaceMap` keeps its own import of
 * them for the pins, unaffected.
 *
 * ## Grouped by where things play, not by where they are filed
 *
 * This screen used to group on the entry's own venue, `building_id ??
 * venue_id`, and that is the wrong question for anything that plays in more
 * than one place. A film's entry is filed at whichever cinema the parse saw
 * first, because `filmRunKey` deliberately leaves the place out — one film
 * across four cinemas is still one decision. So a cinema that is only ever a
 * secondary showing venue nearly vanished. Wolf Kino had 13 upcoming
 * showings, 6 entries playing there, and exactly one entry filed there; seven
 * more cinemas had a real programme and nothing filed at them at all.
 *
 * So placement comes from `showing_list` — every showing with its venue and
 * its building — and a row appears under every place it actually plays. The
 * entry's own venue is the fallback for the rows that have no showing and
 * for showings with no venue, which is what an exhibition run looks like.
 *
 * ## Commercial galleries are not here
 *
 * Doug's call. `venues.commercial` carries INDEX's own sectioning of itself —
 * dealers under `galleries` — and those are not places you decide to visit
 * on an evening. Everything else stays, including the municipal galleries and
 * the project spaces, which is why the flag and not `kind` is what hides a
 * row. See migration 20260921090000.
 *
 * ## The place page is unchanged (WP 3.4a)
 *
 * Everything from `if (place)` down is 3.4a's and 3.4c's: a hero with a
 * Directions button where there are coordinates, a Follow button (P3,
 * `venue_follows`, keyed on the building), and a segmented control between
 * `PlaceProgramme` (rows by day, windowed) and `PlaceInfo` (address, website,
 * halls, credit).
 */
import { lazy, memo, Suspense, useEffect, useMemo, useState } from 'react'
import { Check, Navigation } from 'lucide-react'
import { EventRow } from '../components/EventRow'
import { EventSheet } from '../components/EventSheet'
import { PlaceProgramme } from '../components/PlaceProgramme'
import { PlaceInfo } from '../components/PlaceInfo'
import { ScreenHead } from '../components/ScreenHead'
import { Button, Chip, Empty, Segmented, Sheet } from '../ui'
import { useIsAdmin, adminGate } from '../lib/useIsAdmin'
import { useFollows, useVenues, type VenueRow } from '../lib/queries'
import { useFollowVenue, useUnfollowVenue } from '../lib/mutations'
import { indexShowings } from '../lib/derive'
import { iso } from '../lib/calendar'
import { FIRST_DRAWN, nextDrawn, splitDrawn } from '../lib/drawPace'
import { parseHash } from '../lib/router'
import { distanceKm } from '../lib/reach'
import { placePoint, useRegionDistricts, useRegionPlace } from '../lib/currentRegion'
import { groupNames, whereOf } from '../lib/deskFilters'
import { closedLine, isClosed } from '../lib/venueOpen'
import { GROUPS, groupOf } from '../lib/placeGroups'
import type { PlaceSummary } from '../components/PlaceMap'
import { heroSrcSet, heroUrl, swatch } from '../lib/entries'
import type { ShowingLite } from '../components/CardBody'
import type { FeedRow } from '../lib/types'
import { Loading, Failed } from './Stub'

const PlaceMap = lazy(() => import('../components/PlaceMap'))

/** A region with this many groups or fewer draws every chip, including the
 *  ones at 0 (Berlin: 12, as it always has). A larger one — Boston has sixty
 *  towns — draws the ones with something in them, and the one chosen: a row of
 *  sixty chips, fifty of them at 0, is not a filter. */
const DRAW_ALL_GROUPS_UP_TO = 15

/** One identity for "nothing yet", so the memos below do not recompute on a
 *  fresh empty array every render. */
const NONE_YET: never[] = []

export interface Place {
  id: string
  name: string
  district: string | null
  kind: string | null
  heroKey: string | null
  heroAlt: string | null
  credit: string | null
  license: string | null
  source: string | null
  rows: FeedRow[]
  /** Where in this building each row actually plays, when that is a hall
   *  under it rather than the building itself. The Philharmonie's Großer Saal
   *  and its Foyer are one place on this screen and two different evenings. */
  hallOf: Map<string, string>
}

/** A place page for a venue `placesOf` left out — a closed one, reached by
 *  link from an old entry. The same fields the index would have filled, with
 *  no rows: `PlaceProgramme` and `PlaceInfo` read the feed for themselves by
 *  venue id, so an empty list here hides nothing that is actually on. */
export function placeOfVenue(v: VenueRow): Place {
  return {
    id: v.id,
    name: v.name,
    district: v.district,
    kind: v.kind,
    heroKey: v.hero_key,
    heroAlt: v.hero_alt,
    credit: v.hero_credit,
    license: v.hero_license,
    source: v.hero_source_url,
    rows: [],
    hallOf: new Map(),
  }
}

/**
 * Group the feed by the building things play in.
 *
 * The building is the unit a person thinks in. "Maxim Gorki Theater" is one
 * place with five stages, and five rows for Bühne, Container, Galerie,
 * Kantine and Rangfoyer is the schema showing through.
 *
 * Placement comes from the showings. A row lands under every building it
 * plays in, so one film is counted at each of its cinemas — which is the
 * point, and also means the counts across places deliberately sum to more
 * than the number of rows.
 */
export function placesOf(
  rows: FeedRow[],
  showings: ShowingLite[],
  venues: Map<string, VenueRow>,
): Place[] {
  const buildingOf = (id: string): string => venues.get(id)?.parent_id ?? id

  // entry id -> the buildings it plays in, and the hall within each.
  const where = new Map<string, Map<string, string | null>>()
  const place = (entryId: string, buildingId: string, hall: string | null) => {
    let m = where.get(entryId)
    if (!m) { m = new Map(); where.set(entryId, m) }
    if (!m.has(buildingId) || (m.get(buildingId) == null && hall)) m.set(buildingId, hall)
  }

  for (const sh of showings) {
    if (!sh.entry_id || !sh.venue_id) continue
    const b = sh.building_id ?? buildingOf(sh.venue_id)
    place(sh.entry_id, b, sh.venue_id === b ? null : (sh.venue ?? null))
  }

  // name -> venue, first one wins (what `.find` over the values returned).
  // Built on first use, once per call: the lookup sits inside the per-row
  // loop, and scanning every venue for each row with no district was the
  // quadratic part.
  let byName: Map<string, VenueRow> | null = null
  const venueByName = (): Map<string, VenueRow> => {
    if (!byName) {
      byName = new Map()
      for (const v of venues.values()) if (!byName.has(v.name)) byName.set(v.name, v)
    }
    return byName
  }

  const by = new Map<string, Place>()
  for (const e of rows) {
    // The entry's own venue, for the rows with no placed showing at all —
    // an exhibition run, and the handful of showings with no venue on them.
    let mine = where.get(e.id)
    if (!mine || mine.size === 0) {
      const vid = e.venue_id
      if (!vid) continue
      const b = e.building_id ?? buildingOf(vid)
      mine = new Map([[b, vid === b ? null : (e.venue ?? null)]])
    }

    for (const [bid, hall] of mine) {
      const v = venues.get(bid)
      // A venue we hold no row for cannot be drawn: no name, no kind, and no
      // way to know whether it is a shop.
      if (!v) continue
      // Doug's call. Commercial galleries are not a place you go on an
      // evening, and INDEX's own sectioning is what says which they are.
      if (v.commercial) continue
      // A place that no longer operates (`venues.closed_at`) is not one to
      // go to either, however many rows still point at it. Its own page
      // stays reachable by link — see `placeOfVenue` below.
      if (isClosed(v)) continue

      let p = by.get(bid)
      if (!p) {
        p = {
          id: bid,
          name: v.name,
          district: v.district,
          kind: v.kind,
          heroKey: v.hero_key,
          heroAlt: v.hero_alt,
          credit: v.hero_credit,
          license: v.hero_license,
          source: v.hero_source_url,
          rows: [],
          hallOf: new Map(),
        }
        by.set(bid, p)
      }
      p.rows.push(e)
      if (hall) p.hallOf.set(e.id, hall)
      // A hall may know a district the building row did not.
      if (!p.district) {
        const hallVenue = hall ? venueByName().get(hall) : null
        if (hallVenue?.district) p.district = hallVenue.district
      }
    }
  }

  for (const p of by.values()) {
    p.rows.sort((a, b) => ((a.run_start ?? '9999') < (b.run_start ?? '9999') ? -1 : 1))
  }

  return [...by.values()]
}

type DetailTab = 'week' | 'month' | 'all' | 'info'
const isDetailTab = (v: string): v is DetailTab =>
  v === 'week' || v === 'month' || v === 'all' || v === 'info'

const SEGMENTS: { value: DetailTab; label: string }[] = [
  { value: 'week', label: 'This week' },
  { value: 'month', label: 'Month' },
  { value: 'all', label: 'All' },
  { value: 'info', label: 'Info' },
]

/** Does this row have an occurrence today — a run in progress or opening
 *  today, or a showing today (its own, or read off the shared showings list
 *  when the row itself carries no single date). What "something on today"
 *  in the brief's own ordering means, one place rather than three call
 *  sites guessing at the same question from different fields. */
function onToday(e: FeedRow, today: string, showingsByEntry: Map<string, ShowingLite[]>): boolean {
  if (e.run_start && e.run_end) return e.run_start <= today && e.run_end >= today
  const own = showingsByEntry.get(e.id)
  if (own?.length) return own.some(s => iso(new Date(s.starts_at)) === today)
  return ((e.next_showing ? iso(new Date(e.next_showing)) : null) ?? e.published_on ?? e.run_start) === today
}

const bestScoreOf = (p: Place): number => p.rows.reduce((m, e) => Math.max(m, e.score ?? 0), 0)

/** A word for a place's kind, worth putting in front of Doug — never the raw
 *  `venues.kind` slug (`project_space`, `dance`), which the old "By kind"
 *  index never showed either: it read the same value only through `groupOf`,
 *  as a section heading. This is that reading, per place instead of per
 *  section, so a group header's "kind · district · following" line does not
 *  become the one place in the app that leaks a schema value onto a screen
 *  (`GROUPS`' own labels, `../lib/placeGroups`, are read once at import time
 *  rather than searched afresh per row). */
const GROUP_LABEL: Record<string, string> = Object.fromEntries(GROUPS.map(g => [g.id, g.label]))
const kindLabelOf = (kind: string | null): string | null => (kind ? GROUP_LABEL[groupOf(kind)] : null)

/** Does this place answer Browse's search box — its name or its district.
 *  Exported so `Browse.tsx` can apply the exact same query to what it hands
 *  `PlacesChips` for its own counts, rather than a second substring check
 *  that could quietly stop agreeing with this one. */
export function placeMatches(p: { name: string; district: string | null }, q: string): boolean {
  const needle = q.trim().toLowerCase()
  if (!needle) return true
  return p.name.toLowerCase().includes(needle) || (p.district ?? '').toLowerCase().includes(needle)
}

/** The chip state Browse's header owns and hands down — the Bezirk chips and
 *  Near me move into that one row (WP 6.3's brief, item 4), so the state
 *  they read lives where the chips are drawn. */
export interface PlacesFilter {
  bezirk: string | null
  nearMe: boolean
  geo: { lat: number; lng: number } | null
  q: string
}

export const NONE_FILTER: PlacesFilter = { bezirk: null, nearMe: false, geo: null, q: '' }

/**
 * @param places    Browse's own `placesOf(base, showings, venues)` — `base`
 *                   is the same shared, already-filtered feed (dismissed,
 *                   language, hidden lanes, already-happened, away) Events
 *                   mode windows by day; this mode groups it by building
 *                   instead, once, up in `Browse.tsx`, so the header's
 *                   Bezirk counts and this screen's own list can never
 *                   disagree about what a place is.
 * @param openId    The place the route is pointing at, from `#/places/<id>`
 *                   (now `#/browse/<id>?mode=places` — `router.ts`'s alias).
 *                   Local state used to hold this, so the phone's back
 *                   gesture from a venue's detail left the tab instead of
 *                   returning to the index — a sheet's Escape/scrim/drag can
 *                   close it, but nothing else in the app answers to Back
 *                   except the hash (UI-audit F08).
 * @param onOpen    Sets the route.
 * @param filter    The Bezirk/Near-me/search state Browse's header owns.
 * @param mapOpen   Whether Browse's header "Map" button is showing the map.
 * @param onCloseMap Closes it.
 */
export function Places({ places: allPlaces, showings, openId, onOpen, filter, mapOpen, onCloseMap }: {
  /** Browse's own `placesOf(base, showings, venues)` — computed once, up
   *  there, so the header's Bezirk counts and this screen's list are never
   *  two calls that could disagree about what a place is. */
  places: Place[]
  showings: ShowingLite[]
  openId: string | null
  onOpen: (id: string | null) => void
  filter: PlacesFilter
  mapOpen: boolean
  onCloseMap: () => void
}) {
  const today = iso(new Date())
  // The Info tab's Edit button (P2) is for admins (U0.3); Postgres refuses the
  // write for anyone else (U0.2). Read here, above every early return — the
  // place page below is a branch, and a hook inside it would not run on the
  // first render.
  const isAdmin = useIsAdmin()
  // U1.2: Near me and the Map distance measure from the person's pin, which
  // may not exist — then there is no distance, not a distance from Berlin.
  // C2.2 (D5): the pin of the region being looked at, so Concord measures
  // from Concord and a region with no row has no Near me at all.
  const home = placePoint(useRegionPlace())
  const districts = useRegionDistricts()

  // The segmented control's own state. Reset (and re-read from `?tab=`)
  // whenever the route points at a different place, or at none — a tab left
  // open on one building must not still be showing on the next.
  const [tab, setTab] = useState<DetailTab>('week')
  useEffect(() => {
    const fromHash = parseHash(window.location.hash).query?.tab
    setTab(fromHash && isDetailTab(fromHash) ? fromHash : 'week')
  }, [openId])

  const [open, setOpen] = useState<FeedRow | null>(null)

  // The places themselves, with a building's inherited hero already resolved
  // by `inheritVenueHeroes`.
  const venuesQ = useVenues()
  // P3: wired here, on the place page's own Follow button.
  const followsQ = useFollows()
  const follow = useFollowVenue()
  const unfollow = useUnfollowVenue()

  const venuesById = venuesQ.data?.byId
  const places = allPlaces

  const showingsByEntry = useMemo(() => indexShowings(showings), [showings])

  const searched = useMemo(
    () => places.filter(p => placeMatches(p, filter.q)),
    [places, filter.q],
  )

  // `venues.district` was normalised by an earlier migration, but folding it
  // through the region's rows again rather than trusting the stored value
  // keeps a venue written after the backfill (or one that somehow slipped past
  // `leadVenue.ts`'s own normalisation) from just not matching any chip.
  const filtered = useMemo(
    () => (filter.bezirk ? searched.filter(p => whereOf(p.district, districts) === filter.bezirk) : searched),
    [searched, filter.bezirk, districts],
  )

  // Item 4's own order: something on today first, then best score — unless
  // Near me is on, which reads the same list by distance instead. Never a
  // third list of its own: Near me is a different sort over the one Browse
  // already filtered to, not a second query (CLAUDE.md's "one value, two
  // jobs" bug runs the other way too — two lists that could silently drift
  // apart on what counts as "shown").
  const ordered = useMemo(() => {
    const origin = filter.geo ?? home
    if (filter.nearMe && origin) {
      const withDistance = filtered.map(p => {
        const v = venuesById?.get(p.id)
        const km = v?.lat != null && v?.lng != null ? distanceKm(v.lat, v.lng, origin) : null
        return { p, km }
      })
      return withDistance
        .sort((a, b) => (a.km ?? Infinity) - (b.km ?? Infinity))
        .map(x => x.p)
    }
    // Each place's two keys once, not once per comparison (a4-13).
    const leadToday = new Map(filtered.map(p => [p, onToday(p.rows[0], today, showingsByEntry)]))
    const best = new Map(filtered.map(p => [p, bestScoreOf(p)]))
    return [...filtered].sort((a, b) =>
      Number(leadToday.get(b)) - Number(leadToday.get(a))
      || best.get(b)! - best.get(a)!)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `today` is fresh every render; the Berlin day is what this cares about.
  }, [filtered, filter.nearMe, filter.geo, venuesById, showingsByEntry, home?.lat, home?.lng])

  // The ordering above only checked one row per place for speed; "something
  // on today" itself has to ask every row a place has, which is why the
  // today/rest split below re-asks it rather than trusting the sort's own
  // comparator (a place can have its "on today" row anywhere in `p.rows`,
  // not only at index 0 — `ordered`'s comparator above is a cheap proxy for
  // sorting, this is the real answer for splitting).
  //
  // Memoised (a4-13): asking every row of every place is the expensive half
  // of this screen, and the paced draw below re-renders it once per step.
  const { withToday, rest } = useMemo(() => {
    if (filter.nearMe) return { withToday: NONE_YET as Place[], rest: ordered }
    const hasTodayRow = (p: Place) => p.rows.some(e => onToday(e, today, showingsByEntry))
    const withToday: Place[] = []
    const rest: Place[] = []
    for (const p of ordered) (hasTodayRow(p) ? withToday : rest).push(p)
    return { withToday, rest }
  }, [ordered, filter.nearMe, showingsByEntry, today])

  const notGeocoded = filter.nearMe ? filtered.length - ordered.filter(p => {
    const v = venuesById?.get(p.id)
    return v?.lat != null && v?.lng != null
  }).length : 0

  const mapPlaces = useMemo<PlaceSummary[]>(() => {
    if (!venuesById) return NONE_YET
    const out: PlaceSummary[] = []
    for (const p of filtered) {
      const v = venuesById.get(p.id)
      if (v?.lat != null && v?.lng != null) {
        out.push({ id: p.id, name: p.name, kind: p.kind, district: p.district, lat: v.lat, lng: v.lng, count: p.rows.length })
      }
    }
    return out
  }, [filtered, venuesById])

  // a4-13: the index draws its first screenful, then the rest a few groups
  // per task (`drawPace.ts`) — all ~240 in one commit was one 2–5 s task at
  // CPU x4. Restarted whenever the list being drawn changes (a chip, the
  // search, a place opened and closed), so no change commits the whole list
  // at once either. `drawn` is derived rather than reset in an effect, so the
  // render that sees a new key already draws only the first screenful.
  const paceKey = `${openId ?? ''}|${filter.q}|${filter.bezirk ?? ''}|${filter.nearMe}`
  const [pace, setPace] = useState({ key: paceKey, drawn: FIRST_DRAWN })
  const drawn = pace.key === paceKey ? pace.drawn : FIRST_DRAWN
  useEffect(() => {
    const next = nextDrawn(drawn, ordered.length)
    if (next === null) return
    const t = setTimeout(() => setPace({ key: paceKey, drawn: next }), 0)
    return () => clearTimeout(t)
  }, [paceKey, drawn, ordered.length])

  const pending = venuesQ.isPending || followsQ.isPending
  const error = venuesQ.error ?? followsQ.error

  if (pending) return <Loading />
  if (error) return <Failed error={error.message} />

  // A route can point at a place that no longer has anything upcoming — the
  // same "bookmark to a list that's gone" case `Lists` already handles, and
  // the same answer: fall through to the index rather than an error.
  //
  // Except a closed place (`venues.closed_at`): `placesOf` leaves it out of
  // the index on purpose, and an old saved entry still links here, so the
  // page is built from the venue row itself — nothing coming up, "Closed"
  // where the count was — rather than the link silently landing on the
  // index with no word about why.
  const closedVenue = openId ? venuesById?.get(openId) ?? null : null
  const place = openId
    ? places.find(p => p.id === openId) ?? (closedVenue && isClosed(closedVenue) ? placeOfVenue(closedVenue) : null)
    : null
  if (place) {
    const allVenues = [...(venuesById?.values() ?? [])]
    const venue = venuesById?.get(place.id) ?? null
    const closed = venue ? closedLine(venue) : null
    const halls = allVenues.filter(v => v.parent_id === place.id)
    const hasCoords = venue?.lat != null && venue?.lng != null
    const distance = hasCoords ? distanceKm(venue.lat, venue.lng, home) : null
    const canEdit = adminGate(isAdmin) === 'show'
    const followed = followsQ.data?.has(place.id) ?? false
    const followBusy = follow.isPending || unfollow.isPending

    const pickTab = (next: string) => {
      if (!isDetailTab(next)) return
      setTab(next)
      const base = `#/places/${encodeURIComponent(place.id)}`
      history.replaceState(null, '', next === 'week' ? base : `${base}?tab=${next}`)
    }

    return (
      <>
        {/* WP 10.7: the place page had no `ScreenHead` at all — `Browse.tsx`
            hides its own ScreenHead while a place is open
            (`!placeDetailOpen`), so this was the one screen the account
            button never reached from. */}
        <ScreenHead lead={
          <button className="backlink" onClick={() => onOpen(null)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
            Places
          </button>
        } />

        <div className="placehero">
          {place.heroKey
            ? <img src={heroUrl({ venue_hero_key: place.heroKey } as FeedRow) ?? ''}
                   srcSet={heroSrcSet({ venue_hero_key: place.heroKey } as FeedRow) ?? undefined}
                   sizes="(max-width: 680px) 100vw, 680px" width={680} height={383}
                   decoding="async"
                   alt={place.heroAlt ?? place.name} />
            : <div className="ph" style={{ background: swatch(place.id) }} />}
          {hasCoords && venue && (
            <a className="u-btn u-btn--secondary u-btn--sm placemap"
               href={`https://maps.google.com/?q=${venue.lat},${venue.lng}`}
               target="_blank" rel="noopener noreferrer">
              <span className="u-btn__face">
                <Navigation size={16} />
                <span className="u-btn__label">{distance != null ? `Map · ${distance.toFixed(1)} km` : 'Map'}</span>
              </span>
            </a>
          )}
        </div>

        <h1 className="placename">{place.name}</h1>
        <div className="placemetarow">
          <p className="placemeta">
            {[place.district, closed ?? `${place.rows.length} coming up`].filter(Boolean).join(' · ')}
          </p>
          {/* The action is the button and the state is quiet (UI audit
              a1-29): it was the other way round, so "Follow" read as a bare
              label and only "Following" — the thing already done — looked
              pressable. The tick says the quiet one is a state, not a
              second, weaker action. `aria-pressed` carries the same. */}
          <Button
            variant={followed ? 'quiet' : 'secondary'}
            size="sm"
            icon={followed ? <Check size={16} aria-hidden="true" /> : undefined}
            aria-pressed={followed}
            busy={followBusy}
            onClick={() => (followed ? unfollow.mutate(place.id) : follow.mutate(place.id))}
          >
            {followed ? 'Following' : 'Follow'}
          </Button>
        </div>

        <Segmented
          className="placeseg"
          value={tab}
          onChange={pickTab}
          options={SEGMENTS.map(s => ({ value: s.value, label: s.label }))}
        />

        {tab === 'info'
          ? (venue
              ? <PlaceInfo venue={venue} halls={halls} canEdit={canEdit} />
              : <Empty title="Nothing more is known about this place." />)
          : <PlaceProgramme venueId={place.id} scope={tab} />}
      </>
    )
  }

  // a4-13: one memoised component per group, so each step of the paced draw
  // below renders only the groups it adds — as a plain function every step
  // re-rendered every `EventRow` already drawn, which made the draw quadratic.
  const group = (p: Place) => (
    <PlaceGroup key={p.id} p={p} showingsByEntry={showingsByEntry}
                followed={followsQ.data?.has(p.id) ?? false} onOpen={onOpen} onOpenRow={setOpen} />
  )

  const split = splitDrawn(drawn, withToday.length)

  return (
    <>
      {mapOpen && (
        <Sheet title="Map" open onOpenChange={o => { if (!o) onCloseMap() }}>
          <p className="placemeta">
            {mapPlaces.length} of {filtered.length} shown on the map
            {filtered.length > mapPlaces.length
              ? ` — ${filtered.length - mapPlaces.length} ${filtered.length - mapPlaces.length === 1 ? "hasn't" : "haven't"} been geocoded yet.`
              : '.'}
          </p>
          <Suspense fallback={<div className="pmap pmap--loading" />}>
            <PlaceMap places={mapPlaces} onOpen={id => { onCloseMap(); onOpen(id) }} />
          </Suspense>
        </Sheet>
      )}

      {/* a1-15: what the list below holds — after the search and the Bezirk
          chip — not every place Browse knows of, which kept saying 238 under
          a Mitte filter showing 72. */}
      <p className="placemeta">
        {placesCount(filtered.length)} with something on. Halls sit under the building they are in.
      </p>

      {ordered.length === 0 && <Empty title="Nothing matches." />}

      {withToday.length > 0 && (
        <>
          <p className="kh">Something on today · {placesCount(withToday.length)}</p>
          {withToday.slice(0, split.first).map(group)}
        </>
      )}

      {rest.length > 0 && split.first === withToday.length && (
        <>
          <p className="kh">{filter.nearMe ? 'Nearest first' : 'Later this week'}</p>
          {rest.slice(0, split.second).map(group)}
          {filter.nearMe && notGeocoded > 0 && split.second >= rest.length && (
            <p className="placemeta">{notGeocoded} more with nothing geocoded yet, not shown here.</p>
          )}
        </>
      )}

      <EventSheet e={open} onClose={() => setOpen(null)} />
    </>
  )
}

/** "1 place", "72 places". */
const placesCount = (n: number) => `${n} ${n === 1 ? 'place' : 'places'}`

/** One building in Places' index: its header, its lead row, and the line to
 *  the rest of its programme. Memoised (a4-13) — see `group` in `Places`. */
const PlaceGroup = memo(function PlaceGroup({ p, showingsByEntry, followed, onOpen, onOpenRow }: {
  p: Place
  showingsByEntry: Map<string, ShowingLite[]>
  followed: boolean
  onOpen: (id: string | null) => void
  onOpenRow: (e: FeedRow) => void
}) {
  const lead: FeedRow | undefined = p.rows[0]
  const hall = lead ? p.hallOf.get(lead.id) : undefined
  const display: FeedRow | null = lead ? (hall ? { ...lead, venue: hall } : lead) : null
  const leadShowings = lead ? (showingsByEntry.get(lead.id) ?? NONE_YET) : NONE_YET
  const more = p.rows.length - 1
  return (
    <div className="placegroup">
      <button className="ph2" onClick={() => onOpen(p.id)}>
        <div className="ph2__img">
          {p.heroKey
            ? <img src={heroUrl({ venue_hero_key: p.heroKey } as FeedRow, 112) ?? ''}
                   width={44} height={44} alt="" loading="lazy" decoding="async" />
            : <span className="phfill" style={{ background: swatch(p.id) }} />}
        </div>
        <div className="ph2__text">
          <p className="ph2__name">{p.name}</p>
          <p className="ph2__meta">
            {[kindLabelOf(p.kind), p.district, followed ? 'following' : null].filter(Boolean).join(' · ')}
          </p>
        </div>
        <span className="ph2__count">{p.rows.length}<br />coming</span>
      </button>
      {display && (
        <EventRow e={display} showings={leadShowings} showHall={!!hall} onOpen={onOpenRow} />
      )}
      {more > 0 && (
        <button className="morep" onClick={() => onOpen(p.id)}>
          <b>{more} more</b> at {p.name} · full programme
        </button>
      )}
    </div>
  )
})

/** The Bezirk chip row, drawn by `Browse.tsx`'s own header (WP 6.3, item 4)
 *  — exported from here rather than written a second time in `Browse.tsx`,
 *  so the chips and the filtering above read the same district rows and the
 *  same counts can never drift apart.
 *
 *  Near me left this row (a1-33, Doug's choice): it is a sort, not a place,
 *  and sharing the row put two chips on at once — Near me and All — which
 *  read as two filters. It is `PlacesSort` now, below the chips. */
export function PlacesChips({ places, filter, onChange }: {
  places: { district: string | null }[]
  filter: PlacesFilter
  onChange: (next: PlacesFilter) => void
}) {
  const districts = useRegionDistricts()
  const groups = useMemo(() => groupNames(districts), [districts])
  const counts = useMemo(() => {
    const m = new Map<string, number>()
    for (const p of places) {
      const b = whereOf(p.district, districts)
      if (b) m.set(b, (m.get(b) ?? 0) + 1)
    }
    return m
  }, [places, districts])
  const drawn = groups.length <= DRAW_ALL_GROUPS_UP_TO
    ? groups
    : groups.filter(g => (counts.get(g) ?? 0) > 0 || g === filter.bezirk)

  return (
    <>
      <Chip on={filter.bezirk === null} count={places.length} onClick={() => onChange({ ...filter, bezirk: null })}>
        All
      </Chip>
      {drawn.map(b => (
        <Chip key={b} on={filter.bezirk === b} count={counts.get(b) ?? 0}
              onClick={() => onChange({ ...filter, bezirk: filter.bezirk === b ? null : b })}>
          {b}
        </Chip>
      ))}
    </>
  )
}

/** How Places orders its buildings (a1-33): something on today first, the
 *  index's own order, or nearest first. A sort, so a two-way switch apart
 *  from the Bezirk chips, which filter — both can be on at once, and the
 *  screen says so with one pressed chip and one pressed segment rather than
 *  two pressed chips in one row. */
export function PlacesSort({ filter, onChange }: {
  filter: PlacesFilter
  onChange: (next: PlacesFilter) => void
}) {
  // No pin, no Near me (U1.2): a wrong "nearest first" is worse than none.
  // The pin is the current region's (C2.2, D5).
  if (!placePoint(useRegionPlace())) return null
  return (
    <Segmented
      className="placesort"
      aria-label="Sort places"
      value={filter.nearMe ? 'near' : 'today'}
      onChange={v => onChange({ ...filter, nearMe: v === 'near' })}
      options={[{ value: 'today', label: 'On today first' }, { value: 'near', label: 'Near me' }]}
    />
  )
}
